/**
 * French translations — default locale.
 */
const fr = {
  // ── App-level ─────────────────────────────────────
  app: {
    skip_to_content: "Aller au contenu principal",
  },

  // ── Navigation ────────────────────────────────────
  nav: {
    home: "Accueil",
    predictions: "Courses",
    championship: "Champ.",
    leagues: "Ligues",
    profile: "Profil",
    live: "Direct",
    standings: "Classements",
    preds_short: "Pronos",
    aria_label: "Navigation principale",
  },

  // ── Auth ──────────────────────────────────────────
  auth: {
    tagline: {
      main: "Fais tes pronos, défie tes potes,\nvivez la F1 comme jamais.",
      footer: "Pronostique. Défie.",
      footer_accent: "Vis-le.",
    },
    tabs: {
      login: "Connexion",
      register: "Inscription",
    },
    form: {
      email_label: "Email",
      email_placeholder: "you.com",
      password_label: "Mot de passe",
      remember_me: "Se souvenir",
      forgot_password: "Oublié ?",
    },
    login: {
      submit: "Se connecter",
      loading: "Connexion...",
      success: "Connexion réussie !",
    },
    register: {
      username_label: "Pseudo pilote",
      username_placeholder: "VerstappenFan, LeclercSZN...",
      password_placeholder: "8 caractères minimum",
      nationality_label: "Nationalité",
      nationality_placeholder: "Sélectionne ton pays",
      submit: "Créer mon compte",
      loading: "Création...",
      success: "Compte créé !",
    },
    magic_link: {
      send: "Recevoir un lien magique",
      sent: "Lien envoyé ✓",
      verifying: "Vérification du lien magique...",
      email_required: "Entre ton email pour recevoir le lien magique",
      send_error: "Erreur lors de l'envoi du lien magique",
      send_success: "Lien magique envoyé !",
      success: "Connexion magique confirmée !",
      invalid: "Lien magique invalide ou expiré",
    },
    invite: {
      label: "Invitation détectée",
      description: "Connecte-toi pour rejoindre la ligue {{code}}.",
    },
    social: {
      divider: "ou continuer avec",
    },
    footer: {
      new_user: "Nouveau ?",
      register_link: "Rejoins la course",
      existing_user: "Déjà inscrit ?",
      login_link: "Se connecter",
    },
    error: {
      generic: "Une erreur est survenue",
    },
  },

  // ── Username setup ────────────────────────────────
  username: {
    progress: "Étape 3/3",
    title: "Choisis ton pseudo",
    subtitle: "C'est comme ça que tes amis te verront.",
    placeholder: "SpeedyMax",
    validation: {
      char_count: "3-20 car.",
      allowed: "Lettres, chiffres, _",
    },
    preview_badge: "Nouveau",
    submit: "C'est parti !",
    loading: "Enregistrement...",
    success: "Pseudo enregistré !",
    taken: "Ce pseudo est déjà pris",
  },

  // ── Password ──────────────────────────────────────
  password: {
    forgot: {
      title: "Mot de passe oublié ?",
      subtitle: "Entre ton email pour recevoir un lien de réinitialisation.",
      email_placeholder: "you.com",
      submit: "Envoyer le lien",
      loading: "Envoi...",
      success_title: "Email envoyé",
      success_message:
        "Si un compte existe pour cet email, tu recevras un lien pour réinitialiser ton mot de passe.",
      resend_timer: "Renvoyer dans {{seconds}}s",
      resend: "Renvoyer l'email",
      open_mailbox: "Ouvrir ma boîte mail",
      back: "Retour à la connexion",
      error: "Une erreur est survenue. Réessaye.",
    },
    reset: {
      title: "Nouveau mot de passe",
      subtitle: "Choisis un mot de passe sécurisé.",
      password_label: "Nouveau mot de passe",
      confirm_label: "Confirmer",
      submit: "Réinitialiser",
      loading: "Réinitialisation...",
      success_title: "Mot de passe modifié",
      success_message: "Tu peux maintenant te connecter avec ton nouveau mot de passe.",
      success_login: "Se connecter",
      invalid_title: "Lien invalide",
      request_new: "Demander un nouveau lien",
      invalid_link: "Lien de réinitialisation invalide",
      mismatch: "Les mots de passe ne correspondent pas",
      invalid_criteria: "Le mot de passe ne remplit pas tous les critères",
      match_ok: "Les mots de passe correspondent",
      match_fail: "Ne correspond pas",
      error: "Erreur lors de la réinitialisation",
      rules: {
        characters: "8+ caractères",
        uppercase: "1 majuscule",
        lowercase: "1 minuscule",
        digit: "1 chiffre",
      },
      strength: {
        weak: "Faible",
        medium: "Moyen",
        good: "Bon",
        strong: "Fort",
      },
    },
  },

  // ── Splash screen ────────────────────────────────
  splash: {
    skip: "Passer",
    start: "Commencer",
    start_dashboard: "Accéder au paddock",
    start_login: "Se connecter",
    start_admin: "Accéder au back-office",
    baseline: "Pronostique. Défie. Vibre.",
    loading_label: "Synchronisation paddock",
    logs: [
      "Initialisation du paddock",
      "Chargement du calendrier 2026",
      "Préparation des pronostics",
      "Ouverture de la grille",
    ],
    aria_content: "Écran de lancement PronoKif F1",
    aria_loading: "Chargement de PronoKif F1",
    aria_steps: "Étapes de chargement",
  },

  // ── Email verification ────────────────────────────
  email_verification: {
    message: "Vérifie ton email pour sécuriser ton compte.",
    resend: "Renvoyer",
    sending: "Envoi...",
    sent: "Email de vérification envoyé !",
    error: "Erreur lors de l'envoi",
    close: "Fermer",
  },

  // ── Public menu content ──────────────────────────
  menu: {
    tutorial: {
      title: "Tutoriel",
      subtitle: "Comment jouer à PRONOKIF",
      steps: [
        {
          title: "1. Crée ou rejoins une ligue",
          description:
            "Appuie sur l'onglet Ligues en bas de l'écran. Tu peux créer ta propre ligue et inviter tes potes avec un code, ou rejoindre une ligue existante.",
        },
        {
          title: "2. Fais tes pronostics",
          description:
            "Avant chaque Grand Prix, va dans l'onglet Pronos. Prédi la pole position, le top 10 des qualifications, le vainqueur, le top 10 de la course, et les bonus.",
        },
        {
          title: "3. Week-end Sprint",
          description:
            "Lors d'un week-end Sprint, tu as deux séries de pronostics : une pour le sprint (ferme avant SQ1) et une pour la course principale (ferme avant Q1).",
        },
        {
          title: "4. Bonus",
          description:
            "N'oublie pas les paris bonus : Safety Car (oui/non), pilotes abandons (DNF), meilleur tour, et leader au premier virage. Ils peuvent rapporter gros.",
        },
        {
          title: "5. Marque des points",
          description:
            "Après chaque course, les résultats sont saisis et tes points sont calculés automatiquement. Consulte l'historique de ton profil pour les détails.",
        },
        {
          title: "6. Mini-jeux",
          description:
            "Joue aux mini-jeux (Réaction et Batak) pour gagner des points bonus et défier tes potes. Le meilleur joueur de chaque ligue gagne +2 points !",
        },
        {
          title: "7. Classement",
          description:
            "Suis le classement de ta ligue et le classement général. Bats tes potes et deviens le champion de la saison.",
        },
      ],
    },
    privacy: {
      title: "Confidentialité",
      subtitle: "Protection des données",
      collected_title: "Données collectées",
      collected_intro:
        "PRONOKIF ne collecte que les données nécessaires au fonctionnement de l'appli :",
      collected_items: [
        "Adresse email (pour l'authentification)",
        "Pseudo (pour l'affichage au classement)",
        "Pronostics et scores de jeu",
        "Appartenances aux ligues",
      ],
      usage_title: "Utilisation des données",
      usage_intro: "Tes données sont utilisées exclusivement pour :",
      usage_items: [
        "Gérer ton compte et tes pronostics",
        "Calculer et afficher les classements",
        "Permettre les interactions entre membres (chat, ligues)",
      ],
      security_title: "Sécurité",
      security_intro: "Tes données sont protégées :",
      security_items: [
        "Mots de passe chiffrés (bcrypt)",
        "Connexion sécurisée (HTTPS)",
        "Aucune donnée vendue à des tiers",
      ],
      rights_title: "Tes droits",
      rights_intro: "Conformément au RGPD, tu peux :",
      rights_items: [
        "Accéder à tes données personnelles",
        "Demander des modifications ou la suppression",
        "Exporter tes données",
      ],
      contact_prefix: "Contacte-nous à",
      contact_suffix: "pour exercer tes droits.",
    },
    legal: {
      title: "Mentions légales",
      subtitle: "Informations juridiques",
      publisher_title: "Éditeur de l'appli",
      publisher_text:
        "PRONOKIF est une appli de jeu gratuite et non commerciale, créée par un passionné de Formule 1.",
      contact_label: "Contact :",
      hosting_title: "Hébergement",
      hosting_text: "L'application est hébergée sur la plateforme Emergent.",
      ip_title: "Propriété intellectuelle",
      ip_text:
        "PRONOKIF n'est pas affilié à la Formule 1, la FIA ou FOM. Les marques, logos et noms de pilotes/écuries appartiennent à leurs propriétaires respectifs.",
      ip_owner: "Les visuels et le code de l'appli sont la propriété de l'éditeur.",
      liability_title: "Responsabilité",
      liability_text:
        "PRONOKIF est un jeu de pronostics sans enjeu financier. L'éditeur décline toute responsabilité quant à l'utilisation de l'appli.",
      terms_title: "Conditions d'utilisation",
      terms_items: [
        "L'application est gratuite et le restera",
        "L'inscription implique l'acceptation des règles",
        "Tout comportement inapproprié peut entraîner une exclusion",
        "L'administrateur se réserve le droit de modifier les règles",
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "Écris-nous",
      intro:
        "Une question, une suggestion ou un bug à signaler ? N'hésite pas à contacter l'administrateur de PRONOKIF.",
      email_label: "Adresse email",
      send_email: "Envoyer un email",
      quick_feedback_prefix: "Tu peux aussi utiliser le bouton",
      quick_feedback_suffix:
        "en haut à droite de l'écran pour envoyer un retour rapide directement depuis l'appli.",
    },
    hamburger: {
      open: "Ouvrir le menu",
      close: "Fermer le menu",
      dialog: "Menu principal",
      menu: "Menu",
      back: "Retour",
    },
    rules: {
      title: "Règles du jeu",
      subtitle: "Comment ça marche",
      principle_title: "Principe général",
      principle_text:
        "PRONOKIF est un jeu de pronostics F1. Avant chaque Grand Prix, tu prédis les résultats des qualifications et de la course. Plus tes pronostics sont précis, plus tu marques de points !",
      required_title: "Pronostics obligatoires",
      required_items: [
        { label: "Pole Position", text: "Choisis le pilote qui décrochera la pole" },
        {
          label: "Top 10 Qualifications",
          text: "Choisis les 10 premiers qualifiés dans l'ordre",
        },
        { label: "Vainqueur", text: "Choisis le vainqueur de la course" },
        { label: "Top 10 Course", text: "Choisis les 10 premiers à l'arrivée dans l'ordre" },
      ],
      bonus_title: "Paris bonus",
      bonus_items: [
        { label: "Safety Car", text: "Y aura-t-il un Safety Car pendant la course ?" },
        { label: "Meilleur tour", text: "Quel pilote signera le meilleur tour ?" },
        { label: "Leader virage 1", text: "Qui sera en tête au premier virage ?" },
        { label: "Abandons (DNF)", text: 'Quels pilotes abandonneront ? (ou "Pas de DNF")' },
      ],
      sprint_title: "Week-ends Sprint",
      sprint_intro: "Lors des week-ends Sprint, tu as",
      sprint_strong: "deux séries de pronostics",
      sprint_suffix: "à faire :",
      sprint_items: [
        "Sprint : ferme 15 min avant les Sprint Qualifications (SQ1)",
        "Course principale : ferme 15 min avant les Qualifications (Q1)",
      ],
      deadline_title: "Délais",
      deadline_intro: "Les pronostics doivent être enregistrés",
      deadline_strong: "15 minutes avant le début de la première session",
      deadline_suffix: "concernée (Q1 pour la course, SQ1 pour le sprint).",
      deadline_warning: "Après la deadline, les pronostics ne sont plus modifiables !",
      leagues_title: "Ligues",
      leagues_text:
        "Crée ou rejoins une ligue pour défier tes potes ! Chaque ligue a son propre classement. Tu peux appartenir à plusieurs ligues en même temps.",
    },
    scoring: {
      title: "Barème des points",
      subtitle: "Comment les points sont calculés",
      main: [
        {
          category: "Qualifications",
          items: [
            { label: "Pole Position exacte", points: "+5 pts" },
            { label: "Pilote dans le Top 10 (bonne position)", points: "+3 pts" },
            { label: "Pilote dans le Top 10 (mauvaise position)", points: "+1 pt" },
          ],
        },
        {
          category: "Course",
          items: [
            { label: "Vainqueur exact", points: "+5 pts" },
            { label: "Pilote dans le Top 10 (bonne position)", points: "+3 pts" },
            { label: "Pilote dans le Top 10 (mauvaise position)", points: "+1 pt" },
          ],
        },
      ],
      bonus_title: "Paris bonus",
      bonus: [
        { label: "Safety Car (bonne réponse)", points: "+3 pts" },
        { label: "Meilleur tour exact", points: "+5 pts" },
        { label: "Leader 1er virage exact", points: "+5 pts" },
        { label: "Abandon correct (par pilote)", points: "+3 pts" },
        { label: "« Pas de DNF » correct", points: "+5 pts" },
      ],
      special_title: "Bonus spéciaux",
      special: [
        { label: "Top 10 qualif parfait (10/10 bonnes positions)", points: "+10 pts BONUS" },
        { label: "Top 10 course parfait (10/10 bonnes positions)", points: "+10 pts BONUS" },
        { label: "Meilleur score mini-jeu de la ligue", points: "+2 pts" },
      ],
      sprint_label: "Week-end Sprint :",
      sprint_text:
        "Le même barème de points s'applique aux pronostics Sprint (Sprint Qualifications + Sprint Course + Bonus Sprint).",
      max_title: "Points maximum par GP (course classique)",
      max_rows: [
        { label: "Qualif (Pole + Top 10 parfait)", points: "45 pts" },
        { label: "Course (Vainqueur + Top 10 parfait)", points: "45 pts" },
        { label: "Bonus (SC + FL + T1 + DNF)", points: "~20 pts" },
      ],
      max_total: "Total max",
      max_total_points: "~110 pts",
    },
  },

  feedback: {
    categories: {
      bug: "Signaler un bug",
      suggestion: "Suggestion",
      feedback: "Retour d'exp.",
    },
    max_length_error: "Message trop long (max 2000 caractères)",
    success_toast: "Message envoyé !",
    error_toast: "Erreur lors de l'envoi. Réessaye.",
    title: "Aider l'administrateur",
    subtitle: "Votre avis compte !",
    thanks_title: "Merci !",
    thanks_message: "Ton message a été envoyé à l'administrateur.",
    intro_prefix: "Aide-moi à améliorer l'appli ! Partage tes",
    intro_feedback: " retours",
    intro_bugs: " bugs",
    intro_suggestions: " suggestions",
    category_label: "Catégorie",
    message_label: "Votre message",
    placeholders: {
      bug: "Décris le problème rencontré...",
      suggestion: "Quelle fonctionnalité aimerais-tu voir ?",
      feedback: "Partage ton expérience...",
    },
    char_count: "{{count}}/2000 caractères",
    sending: "Envoi...",
    submit: "Envoyer mon message",
  },

  predictions: {
    race_not_found: "Course introuvable",
    back: "Retour",
    saved_sprint: "Sprint enregistré !",
    saved_main: "Pronostics enregistrés !",
    header_subtitle: "Pronostic course",
    delete_title: "Supprimer mes pronostics",
    sprint: "Sprint",
    race: "Course",
    closed_short: "Fermé",
    status: {
      sprint_ready: "Sprint prêt",
      sprint_incomplete: "Sprint incomplet",
      race_ready: "Course prête",
      race_incomplete: "Course incomplète",
    },
    timer: {
      deadline: "Fermeture pronos",
      sprint: "Ferme 15 min avant SQ1",
      race: "Ferme 15 min avant Q1",
    },
    saving: "Enregistrement...",
    closed: "Pronostics fermés",
    save: "Enregistrer",
    incomplete: "Incomplet",
    form: {
      guided_path: "Parcours guidé",
      build_ticket: "Construis ton ticket",
      essential: "essentiel",
      validated: "validé",
      active_step: "Étape active",
      to_choose: "À choisir",
      free_slot: "Slot libre",
      quick_steps_label: "Étapes rapides",
      todo: "à faire",
      done: "fait",
      saved: "sauvé",
      now_selecting: "À jouer maintenant",
      driver_grid: "Grille pilotes",
      pick_driver: "Choisis ton pilote",
      drivers_short: "pilotes",
      continue_to_next: "Continuer",
      optional: "optionnel",
      ticket_status: "Statut ticket",
      ticket_ready: "Le ticket essentiel est prêt à enregistrer.",
      next_target: "Prochaine cible : {{label}} {{sublabel}}.",
      keep_going: "Continue dans le cockpit.",
      games: "Jeux",
      mini: "Mini",
      validate_back_bonus: "Valider et retour aux bonus",
      stage_qualifying: "Qualif",
      stage_sprint_q: "Sprint Q",
      stage_race: "Course",
      stage_sprint: "Sprint",
      stage_bonus: "Bonus",
      stage_minigames: "Mini-jeux",
      steps: {
        pole: "Pole",
        top10: "Top 10",
        winner: "Vainqueur",
        bonus: "Bonus",
        games: "Jeux",
        sprint_q: "Sprint Q",
        sprint: "Sprint",
        qualif: "Qualif",
        race: "Course",
        bets: "Paris",
        mini: "Mini",
      },
      help: {
        pole: "Choisis le pilote qui partira devant tout le monde.",
        winner: "Sélectionne ton vainqueur. Le top 10 suivra juste après.",
        top10: "Construis l'ordre exact, de P1 à P10. Un second tap retire le pilote.",
        fastest_lap: "Choisis le pilote le plus rapide sur un tour lancé.",
        first_corner: "Qui mènera au premier virage ?",
        dnf: "Sélectionne jusqu'à 5 abandons, puis valide ton choix.",
        sprint_bonus: "Ajoute les paris bonus du sprint.",
        race_bonus: "Ajoute les paris bonus de la course.",
        default: "Avance étape par étape, le cockpit te guide automatiquement.",
      },
      bonus: {
        title: "Questions Bonus",
        points: "+50 pts",
        safety_car: "Safety Car",
        yes: "Oui",
        no: "Non",
        fastest_lap: "Meilleur tour",
        first_corner: "Leader au virage 1",
        dnf: "Abandons",
        no_dnf: "Pas d'abandon",
        selected: "Sélectionné",
        select: "Sélectionner →",
        drivers_count: "{{count}} pilote(s)",
      },
      selection: {
        sprint_quali_pole: "Sélectionne le poleman des qualifs sprint",
        sprint_quali_top10: "Sélectionne le Top 10 des qualifs sprint ({{count}}/10)",
        sprint_race_winner: "Sélectionne le vainqueur du sprint",
        sprint_race_top10: "Sélectionne le Top 10 du sprint ({{count}}/10)",
        sprint_bonus: "Configure tes bonus sprint",
        sprint_fastest_lap: "Sélectionne le pilote qui fera le meilleur tour du sprint",
        sprint_first_corner: "Sélectionne le leader au premier virage du sprint",
        sprint_dnf_select: "Sélectionne les abandons sprint ({{count}}/5)",
        quali_pole: "Sélectionne le poleman",
        quali_top10: "Sélectionne le Top 10 des qualifications ({{count}}/10)",
        race_winner: "Sélectionne le vainqueur de la course",
        race_top10: "Sélectionne le Top 10 de la course ({{count}}/10)",
        bonus: "Configure tes bonus",
        fastest_lap: "Sélectionne le pilote qui fera le meilleur tour",
        first_corner: "Sélectionne le leader au premier virage",
        dnf_select: "Sélectionne les abandons ({{count}}/5)",
      },
    },
  },

  leagues_page: {
    pilot_fallback: "pilote",
    onboarding_badge: "Étape ligue",
    welcome: "Bienvenue {{name}}",
    onboarding_text:
      "Dernière étape avant le paddock : crée ta ligue ou rejoins celle de tes amis.",
    onboarding_steps: ["Profil", "Ligue", "Pronos"],
    choices: {
      create_title: "Créer une ligue",
      create_description: "Ouvre ton paddock privé et invite ton groupe.",
      join_title: "J'ai un code",
      join_description: "Entre une invitation reçue pour rejoindre la grille.",
    },
    joined_toast: 'Tu as rejoint "{{name}}" !',
    invalid_code: "Code invalide",
    created_toast: "Ligue créée !",
    create_error: "Erreur lors de la création",
    code_copied: "Code copié !",
    copy_error: "Impossible de copier",
    share_text: 'Rejoins ma ligue F1 "{{name}}" sur PRONOKIF !',
    selected_toast: "Ligue sélectionnée !",
    title: "Mes ligues",
    join_tab: "Rejoindre",
    create_tab: "Créer",
    join_intro: "Entre le code de la ligue que tu veux rejoindre",
    code_label: "Code d'invitation",
    joining: "Connexion...",
    join_submit: "Rejoindre la ligue",
    create_intro: "Crée ta propre ligue et invite tes amis",
    name_label: "Nom de la ligue",
    name_placeholder: "Ex: Les Champions F1",
    creating: "Création...",
    create_submit: "Créer ma ligue",
    social_unlock_title: "Ta ligue active débloque le jeu social",
    social_unlock_text:
      "Classements entre amis, chat de ligue, mini-jeux et pronostics personnalisés apparaîtront dès que cette étape est terminée.",
  },

  search: {
    groups: {
      navigation: "Navigation",
      drivers: "Pilotes",
      races: "Courses",
      leagues: "Ligues",
      players: "Joueurs",
      history: "Historique",
    },
    date_tbc: "Date à confirmer",
    status: {
      finished: "Terminé",
      in_progress: "En direct",
      cancelled: "Annulé",
      upcoming: "À venir",
    },
    static: {
      dashboard_title: "Accueil",
      dashboard_subtitle: "Dashboard, prochain Grand Prix, activité de ligue",
      predictions_title: "Pronostics",
      predictions_subtitle: "Calendrier des courses et parcours de pronostic",
      championship_title: "Championnat 2026",
      championship_subtitle: "Pilotes, écuries, championnat et calendrier",
      results_title: "Résultats",
      results_subtitle: "Scores, résultats officiels et points gagnés",
      global_leaderboard_title: "Classement global",
      global_leaderboard_subtitle: "Top joueurs de l'app",
      compare_title: "Comparer deux pilotes",
      compare_subtitle: "Face-à-face, statistiques et palmarès",
      profile_title: "Mon profil",
      profile_subtitle: "Compte, progression, ligues et statistiques personnelles",
    },
    league_badge: "Ligue",
    player_badge: "Joueur",
    history_badge: "Historique",
    league_subtitle: "{{count}} joueur(s) • code {{code}}",
    player_subtitle: "#{{position}} • {{points}} pts • niveau {{level}}",
    history_pending: "Pronostic en attente de résultats",
  },

  search_dialog: {
    title: "Recherche profonde",
    description: "Rechercher dans les pilotes, courses, ligues, joueurs et pages de PronoKif.",
    eyebrow: "Recherche profonde",
    subtitle: "Pilotes, courses, ligues, joueurs, résultats et navigation.",
    placeholder: "Rechercher RUS, Monaco, Ferrari, classement, joueur...",
    empty: "Aucun résultat pour cette recherche.",
    indexing: "Indexation des données...",
    help: "Entrée pour ouvrir • Échap pour fermer",
    local_index: "Index local enrichi",
  },

  // ── Common labels ────────────────────────────────
  common: {
    pts: "pts",
    members: "membres",
    players: "joueurs",
    gp: "GP",
    xp: "XP",
    level_short: "Niv.{{level}}",
    rank_suffix: "e",
    cancel: "Annuler",
    back: "Retour",
    see_all: "Tout voir",
    error: "Erreur",
    loading: "Chargement...",
    n_a: "N/A",
    confirmed: "Validé",
    you: "(toi)",
    days: "jours",
    hours: "heures",
    min: "min",
    sec: "sec",
    in_time: "dans",
  },

  // ── Dashboard ────────────────────────────────────
  dashboard: {
    greeting: {
      night: "Bonne nuit",
      morning: "Bonjour",
      afternoon: "Bon aprem",
      evening: "Bonsoir",
    },
    fallback_name: "Pilote",
    total_points: "Points totaux",
    last_gp: "+{{pts}} dernier GP",
    global_rank: "Rang global",
    precision: "Précision",
    gps_played: "{{count}} GP joués",
    current_streak: "Série en cours",
    my_leagues: "Mes ligues",
    join: "Rejoindre",
    join_league_cta: "Rejoins une Ligue !",
    join_league_desc: "Crée ou rejoins une ligue pour jouer avec tes potes",
    lets_go: "C'est parti !",
    add: "Ajouter",
    share_title: "Rejoins {{name}} sur PronoKif !",
    members_count: "{{count}} membres",
    help_admin: "Aider l'administrateur",
    help_admin_desc: "Signaler un bug ou partager un retour",
    recent_results: "Derniers résultats",
    history: "Historique",
    no_results: "Aucun résultat importé",
    quick_actions: "Actions rapides",
    predict: "Pronostiquer",
    standings: "Classements",
    invite: "Inviter",
    share_app: "Partagez l'app",
    share_text: "Fais tes pronos F1 avec moi !",
    live: "Direct",
    no_race: "Pas de course",
    season: "Saison {{year}}",
    tier: {
      diamond: "Diamant",
      platinum: "Platine",
      gold: "Or",
      silver: "Argent",
      bronze: "Bronze",
      default: "Classement",
    },
    aria_profile: "Voir mon profil",
    aria_search: "Rechercher",
  },

  // ── Race carousel ────────────────────────────────
  race_carousel: {
    calendar: "Le calendrier",
    races_to_predict: "{{count}} course(s) à pronostiquer",
    up_to_date: "Tu es à jour",
    calendar_unavailable: "Calendrier indisponible",
    see_all: "Tout voir",
    status: {
      to_predict: "À pronostiquer",
      predicted: "Prono fait",
      finished: "Terminé",
      cancelled: "Annulé",
      closed: "Pronos fermés",
    },
    aria_calendar: "Calendrier des Grands Prix",
    aria_scroll: "Faire défiler le calendrier",
  },

  // ── Race calendar ────────────────────────────────
  race_calendar: {
    title: "Courses 2026",
    subtitle: "Pronostique jusqu'au départ de chaque course",
    filters: {
      upcoming: "À venir",
      finished: "Terminées",
      all: "Toutes",
    },
    status: {
      to_predict: "À pronostiquer",
      predicted: "Prono fait",
      finished: "Terminé",
      cancelled: "Annulé",
      closed: "Fermé",
      live: "Live",
    },
    cta: {
      results: "Résultats",
      cancelled: "Annulé",
      locked: "Verrouillé",
      edit: "Modifier",
      predict: "Pronostiquer",
      follow_live: "Suivre en Live",
    },
    closing_aria: "Clôture des pronostics dans {{time}}",
    empty_title: "Aucune course",
    empty_season_over: "Saison terminée",
    empty_none: "Aucune course ici",
    aria_race: "Fiche course {{name}}",
  },

  // ── Grand Prix detail ────────────────────────────
  grand_prix: {
    tabs: {
      circuit: "Circuit",
      schedule: "Programme",
      grid: "Grille",
      picks: "Pronos",
    },
    live_results: {
      title_live: "Résultats en direct",
      title_final: "Résultats du Grand Prix",
      awaiting: "En attente des résultats…",
      podium: "Podium",
      your_score: "Ton score sur ce GP",
      pending_score: "En attente",
      points: "{{count}} pts",
      leagues: "Tes ligues",
      no_leagues: "Rejoins une ligue pour te comparer",
      position: "{{pos}}e",
      see_details: "Voir le détail des résultats",
      your_picks: "Tes pronos",
      pole: "Pole",
      winner: "Vainqueur",
      corrected: "Résultats mis à jour le {{date}} suite à une décision officielle.",
    },
    sessions: {
      fp1: "Essais Libres 1",
      fp2: "Essais Libres 2",
      fp3: "Essais Libres 3",
      sprint_quali: "Qualifications Sprint",
      sprint: "Sprint",
      qualifying: "Qualifications",
      race: "Course",
    },
    status: {
      finished: "Course terminée",
      pick_deadline: "Limite des pronos",
      paris_time: "heure fr",
    },
    cta: {
      unavailable: "Pronos non disponibles",
      predict: "Faire mes pronos",
      sprint_picks: "Pronos Sprint",
      results: "Voir les résultats",
    },
    picks: {
      your_picks: "Tes pronos",
      modify: "Modifier mes pronos",
      modify_sprint: "Modifier pronos Sprint",
      locked: "Verrouillés",
      live_hint: "Course en cours — résultats sur l'onglet Circuit.",
      none_finished: "Tu n'as pas pronostiqué ce Grand Prix.",
      qualifying: "Qualifications",
      race: "Course",
      sprint_quali: "Sprint · Qualifs",
      sprint_race: "Sprint · Course",
      pole: "Pole",
      winner: "Vainqueur",
      top10: "Top 10",
      bonus: "Bonus",
      safety_car: "Safety Car",
      fastest_lap: "Meilleur tour",
      first_corner: "Leader virage 1",
      dnf: "Abandons",
      no_dnf: "Pas d'abandon",
      yes: "Oui",
      no: "Non",
    },
    empty: {
      not_found: "Grand Prix introuvable",
      no_schedule: "Programme indisponible",
      no_picks: "Pronos indisponibles",
    },
    deadline: {
      sprint: "Ferme 15 min avant SQ1",
      race: "Ferme 15 min avant Q1",
    },
    grid: {
      unavailable_title: "Grille indisponible",
      unavailable_desc: "La liste des pilotes et écuries n'est pas encore disponible.",
      driver_count_one: "pilote",
      driver_count_other: "pilotes",
      starting_grid_title: "Grille de départ",
      pole_position: "Pole Position",
      quali_not_started_title: "Qualifications non terminées",
      quali_not_started_desc: "La grille de départ sera révélée dès la fin des qualifications.",
      quali_awaiting_title: "En attente des résultats",
      quali_awaiting_desc:
        "Les qualifications viennent de se terminer. La grille de départ sera affichée dès la saisie des résultats.",
    },
    circuit_stats: {
      turns: "virages",
      laps: "tours",
    },
    countdown_in: "{{session}} dans",
    picks_open_soon: "Les pronostics ouvriront bientôt pour ce Grand Prix.",
    error_loading: "Impossible de charger les détails",
    not_exist: "Ce Grand Prix n'existe pas",
  },

  // ── Profile ──────────────────────────────────────
  profile: {
    title: "Mon profil",
    global_rank: "Rang mondial :",
    total_points: "Points totaux",
    predictions: "Pronostics",
    missions: "Missions",
    missions_sub: "Gagne de l'XP",
    custom_picks: "Pronos Perso",
    custom_picks_sub: "Crée tes pronos perso",
    global_leaderboard: "Classement global",
    global_leaderboard_sub: "Tous les joueurs",
    league_leaderboard: "Classement de ligue",
    admin: "Administration",
    app_section: "Application",
    install_app: "Installer l'app",
    install_desc: "Ajoute PronoKif à ton écran d'accueil",
    update_available: "Mise à jour disponible",
    update_desc: "Recharge pour appliquer la dernière version",
    installed: "App installée",
    installed_desc: "PronoKif tourne en mode app native",
    install_instructions: "Pour installer PronoKif sur ton écran d'accueil :",
    ios_instructions: "iOS Safari : Partager → Sur l'écran d'accueil",
    android_instructions: "Android Chrome : Menu → Installer l'application",
    desktop_instructions: "Desktop : Barre d'adresse → Installer",
    clear_cache: "Vider le cache et recharger",
    cache_cleared: "Cache vidé. Rechargement...",
    logout: "Déconnexion",
    avatar_modal: "Choisir un Avatar",
    avatar_updated: "Avatar mis à jour !",
    avatar_error: "Erreur lors de la mise à jour",
    photo_uploaded: "Photo uploadée !",
    photo_error: "Erreur lors de l'upload",
    install_launched: "Installation lancée !",
    cagnotte_title: "Cagnotte",
    cagnotte_desc: "Points gagnés aux mini-jeux",
    cagnotte_pts: "pts",
    cagnotte_reaction: "Réaction",
    cagnotte_batak: "Batak",
    cagnotte_games_played: "{{count}} parties en compétition",
  },

  // ── Avatar ───────────────────────────────────────
  avatar: {
    tabs: {
      generate: "Générer Avatar",
      drivers: "Pilotes",
      teams: "Écuries",
      classics: "Classiques",
    },
    custom_alt: "Avatar personnalisé",
    driver_alt: "Pilote",
    steps: {
      photo: "Ajoute ta photo",
      compose: "Casque & visage",
      background: "Arrière-plan",
    },
    photo_intro: "Commence par une photo de face — le casque viendra se poser dessus.",
    capture: "Capturer",
    cancel: "Annuler",
    upload_photo: "Uploader une photo",
    take_selfie: "Prendre un selfie",
    file_too_large: "Image trop lourde (max 5 Mo)",
    face_analysis: "Analyse du visage…",
    helmet_counter: "{{team}} · {{current}}/{{total}} · glisse pour changer de casque",
    drag_hint: "Glisse / zoome pour ajuster le visage dans la visière",
    helmet_library: "Librairie de casques",
    choose_helmet: "Choisir le casque {{team}}",
    next: "Suivant",
    all_teams: "Toutes",
    bg_label: "Arrière-plan",
    bg_scene: "Décor",
    bg_gradient: "Dégradé",
    bg_color: "Couleur",
    apply: "Appliquer à mon profil",
    applying: "Application…",
    download: "Télécharger (PNG)",
    downloading: "Téléchargement…",
    save_error: "Erreur lors de la sauvegarde",
    download_error: "Erreur lors du téléchargement",
    camera_errors: {
      https_required: "La caméra nécessite une connexion sécurisée (HTTPS ou localhost).",
      not_supported: "La caméra n'est pas supportée par ce navigateur.",
      permission_denied: "Accès caméra refusé. Autorise la caméra dans les réglages du navigateur.",
      not_found: "Aucune caméra détectée sur cet appareil.",
      in_use: "La caméra est déjà utilisée par une autre application.",
      generic: "Impossible d'accéder à la caméra.",
      not_ready: "La caméra n'est pas encore prête — réessaie dans un instant.",
    },
    scenes: {
      tarmac: "Tarmac",
      grid: "Grille",
      pit: "Stand",
      podium: "Podium",
      cockpit: "Cockpit",
      checkered: "Damier",
    },
    driver_count_one: "pilote",
    driver_count_other: "pilotes",
  },

  // ── My leagues section ───────────────────────────
  my_leagues: {
    title: "Mes ligues",
    add: "Ajouter",
    no_league: "Aucune ligue",
    create_join: "Créer / Rejoindre",
    select: "Sélectionner",
    code_copied: "Code copié !",
    copy_error: "Impossible de copier",
    share_text: 'Rejoins ma ligue F1 "{{name}}" sur PRONOKIF !',
    selected: "Ligue sélectionnée !",
    select_error: "Erreur",
  },

  // ── Leaderboard ──────────────────────────────────
  leaderboard: {
    title: "Classement",
    league_title: "Classement de la ligue",
    no_ranking: "Pas encore de classement",
    empty_message: "Les points seront calculés après les courses",
    invite_friends: "Invite tes potes pour commencer !",
    invite_cta: "Inviter des potes",
    no_league: "Pas encore de ligue",
    join_cta: "Crée ou rejoins une ligue",
    create_join: "Créer / Rejoindre",
    last_gp: "dernier GP",
    copy: "Copié",
    code: "Code",
    change: "Changer",
    chat: "Chat",
    league: "Ligue",
    league_selected: 'Ligue "{{name}}" sélectionnée',
    league_error: "Erreur lors du changement de ligue",
    code_copied: "Code copié !",
    copy_error: "Impossible de copier",
    aria_share: "Partager la ligue",
    aria_chat: "Chat de la ligue",
    aria_copy: "Copier le code",
    aria_join: "Rejoindre une ligue",
  },

  // ── Global leaderboard ───────────────────────────
  global_leaderboard: {
    title: "Classement général",
    filters: {
      season: "Saison",
      month: "Ce mois",
      last_gp: "Dernier GP",
    },
    not_enough: "Pas assez de joueurs.",
  },

  // ── Championship ─────────────────────────────────
  championship: {
    title: "Championnat F1",
    season: "Saison {{year}}",
    tabs: {
      drivers: "Pilotes",
      teams: "Écuries",
      results: "Résultats",
    },
    updated: "Mise à jour : {{date}}",
    refresh_success: "Classements mis à jour !",
    compare_title: "Comparer les pilotes",
    attribution: "Données fournies par Jolpica F1 API & OpenF1",
    season_progress: {
      select_gp: "Sélectionner un Grand Prix",
      upcoming: "À venir",
      loading: "Chargement des résultats...",
      tabs: {
        race: "Course",
        qualifying: "Qualifs",
        sprint: "Sprint",
        practice: "Essais",
        bonus: "Bonus",
      },
      empty: "Sélectionne un Grand Prix terminé pour voir les résultats",
      load_error: "Erreur lors du chargement des résultats",
    },
    drivers: {
      no_data: "Aucune donnée disponible",
    },
    constructors: {
      no_data: "Aucune donnée disponible",
      pts: "pts",
      wins_one: "{{count}} victoire",
      wins_other: "{{count}} victoires",
    },
  },

  // ── Results ──────────────────────────────────────
  results: {
    title: "Résultats",
    score_title: "Score Grand Prix",
    qualifying: "Qualifications",
    pole: "Pole position",
    race: "Course",
    winner: "Vainqueur",
    points_detail: "Détail des points",
    no_results: "Aucun résultat",
    pending: "Résultats en attente",
    no_prediction: "Tu n'as pas fait de pronostic pour cette course",
  },

  // ── Notifications ────────────────────────────────
  notifications: {
    filters: {
      all: "Toutes",
      races: "Courses",
      leagues: "Ligues",
      chat: "Chat",
      badges: "Badges",
    },
    categories: {
      messages: "Messages",
      results: "Résultats",
      leagues: "Ligues",
      badges: "Badges",
      general: "Général",
    },
    action_required: "Action requise",
    predict_cta: "Faire mes pronos",
    empty: "Aucune notification",
    empty_category: "Aucune notification dans cette catégorie.",
    mark_all_read: "Tout marquer comme lu",
    bell_label: "Notifications",
    bell_unread: "Notifications ({{count}} non lues)",
    time: {
      yesterday: "hier",
      just_now: "à l'instant",
    },
  },

  // ── Missions ─────────────────────────────────────
  missions: {
    tabs: {
      active: "Actives",
      completed: "Complétées",
      season: "Saison",
    },
    completed_toast: "Mission complétée !",
    claim: "Récupérer",
    no_completed: "Aucune mission terminée.",
    all_done: "Toutes les missions sont terminées !",
    level: "Niveau {{level}}",
  },

  // ── Mini-games ───────────────────────────────────
  mini_games: {
    title: "Mini-Jeux",
    rewards: "Récompenses",
    modes: {
      training: "Entraînement",
      competition: "Compétition",
    },
    games: {
      reaction: "Réaction",
      batak: "Batak",
    },
    weekend: "Weekend : {{name}}",
    batak_title: "Batak Pro",
    score_shared: "Score partagé dans {{name}} !",
    share_error: "Erreur lors du partage",
    // Grades (shared across games)
    grade_incredible: "INCROYABLE !",
    grade_excellent: "Excellent !",
    grade_very_good: "Très bien !",
    grade_good: "Bien !",
    grade_ok: "Correct",
    grade_needs_work: "À travailler",
    // Shared actions
    training_label: "Entraînement",
    start: "COMMENCER",
    start_30s: "COMMENCER (30s)",
    retry: "Réessayer",
    save: "Sauvegarder",
    loading: "Chargement...",
    targets_unit: "cibles",
    // Batak-specific
    batak_instruction: "Clique sur les cibles le plus vite possible !",
    batak_share_league: "Partager dans une ligue",
    batak_share_title: "Partager mon score",
    batak_share_message:
      "🎯 J'ai fait {{score}} cibles au Batak Pro ! {{grade}} Qui peut faire mieux ?",
    batak_no_leagues: "Aucune ligue rejointe",
    // Reaction-specific
    reaction_title: "Temps de réaction",
    reaction_idle: "Appuie sur COMMENCER pour lancer la séquence",
    reaction_waiting: "Attendez les feux...",
    reaction_ready: "PRÊT...",
    reaction_wait_lights_out: "Attendez l'extinction !",
    reaction_go: "GO !",
    reaction_click_now: "CLIQUEZ MAINTENANT",
    reaction_false_start: "FAUX DÉPART !",
    reaction_too_early: "Trop tôt !",
    // Leaderboard
    leaderboard_title: "Classement {{game}}",
    leaderboard_league_weekend: "Ligue - Ce weekend",
    leaderboard_no_scores_weekend: "Aucun score enregistré ce weekend",
    leaderboard_global: "Classement global (tous les temps)",
    leaderboard_no_scores: "Aucun score enregistré",
    // Reward info
    reward_competition: "Mode compétition : 3 essais par jeu par weekend",
    reward_winner: "Le gagnant de chaque jeu de ligue remporte",
    reward_xp: "XP gagnés à chaque partie jouée",
    reward_training: "Mode entraînement illimité pour progresser !",
  },

  // ── League list ───────────────────────────────────
  league_list: {
    no_leagues: "Aucune ligue",
    no_leagues_desc: "Crée ou rejoins une ligue ci-dessous",
    active: "Actif",
    activate: "Activer",
    share: "Partager",
  },

  // ── League detail ────────────────────────────────
  league_detail: {
    not_found: "Ligue non trouvée",
    name_placeholder: "Nom de la ligue",
    edit_title: "Modifier la ligue",
    description_label: "Description de la ligue",
    description_placeholder: "Décris ta ligue en quelques mots... (optionnel)",
    add_description: "Ajoute une description à ta ligue",
    tabs: {
      ranking: "Classement",
      members: "Membres",
    },
    chat: "Discussion",
    share_title: "Partager",
    code_copied: "Code copié !",
    copy_error: "Impossible de copier",
    share_text: 'Rejoins ma ligue F1 "{{name}}" sur PRONOKIF !',
    name_empty: "Le nom ne peut pas être vide",
    updated: "Ligue mise à jour !",
    update_error: "Erreur lors de la mise à jour",
    load_error: "Erreur lors du chargement",
  },

  // ── League settings ──────────────────────────────
  league_settings: {
    creator_actions: "Actions du créateur",
    transfer: "Transférer",
    delete: "Supprimer",
    leave: "Quitter la ligue",
    transfer_modal: {
      title: "Transférer la propriété",
      description: "Sélectionne le nouveau propriétaire de {{name}}",
      no_username: "Sans pseudo",
      no_members: "Aucun autre membre",
      loading: "Transfert...",
      success: "Propriété transférée !",
      error: "Erreur lors du transfert",
      select_error: "Sélectionne un nouveau propriétaire",
    },
    delete_modal: {
      title: "Supprimer la ligue",
      confirm: "Es-tu sûr de vouloir supprimer {{name}} ?",
      warning:
        "Cette action est irréversible. Tous les membres seront retirés et les données de la ligue seront perdues.",
      member_warning: "{{count}} autre(s) membre(s) sera(ont) retiré(s).",
      loading: "Suppression...",
      success: "La ligue a été supprimée",
      error: "Erreur lors de la suppression",
    },
    leave_modal: {
      title: "Quitter la ligue",
      confirm: "Es-tu sûr de vouloir quitter {{name}} ?",
      loading: "Départ...",
      button: "Quitter",
      success: "Tu as quitté la ligue",
      error: "Erreur en quittant la ligue",
    },
  },

  // ── League chat ──────────────────────────────────
  league_chat: {
    title: "Discussion",
    not_found: "Ligue non trouvée",
    not_found_desc: "Cette ligue n'existe pas ou tu n'y as plus accès.",
    no_messages: "Aucun message",
    be_first: "Sois le premier à écrire !",
    just_now: "À l'instant",
    updated: "Messages actualisés",
    placeholder: "Écris un message...",
    send_error: "Erreur lors de l'envoi",
    member_one: "membre",
    member_other: "membres",
  },

  // ── Join league ──────────────────────────────────
  join_league: {
    loading: "Chargement de l'invitation...",
    invalid_link: "Lien invalide",
    already_member: "Déjà membre !",
    go_dashboard: "Aller au tableau de bord",
    invitation: "Invitation",
    invited_text: "Tu as été invité à rejoindre une ligue",
    join_cta: "Rejoindre la ligue",
    joining: "Inscription...",
    login_required: "Tu devras te connecter pour rejoindre",
    joined: 'Tu as rejoint "{{name}}" !',
    join_error: "Erreur lors de l'inscription",
  },

  // ── Member profile ───────────────────────────────
  member_profile: {
    title: "Profil",
    not_found: "Profil introuvable",
    not_found_desc: "Ce joueur n'existe pas ou n'est plus actif.",
    load_error: "Impossible de charger le profil",
    you: "Toi",
    tabs: {
      stats: "Stats",
      predictions: "Pronostics",
    },
    stats: {
      races: "Courses",
      exact_winners: "Vainqueurs exacts",
      exact_poles: "Poles exactes",
      best_reaction: "Meilleure réaction",
      best_batak: "Meilleur Batak",
    },
    shared_leagues: "Ligues en commun",
    recent_predictions: "Derniers Pronostics",
    member_since: "Membre depuis",
    points: "Points",
    validated: "Validé",
    winner_label: "Vainqueur :",
    pole_label: "Pole :",
  },

  // ── Custom predictions ───────────────────────────
  custom_predictions: {
    title: "Pronos Perso",
    create: "Créer",
    gp_label: "Grand Prix",
    info_text:
      "Crée des pronos fun pour ta ligue ! Le créateur définit la bonne réponse après la course.",
    points_info: "+2 points par bonne réponse",
    my_predictions: "Mes pronos créés",
    league_predictions: "Pronos de la ligue ({{count}})",
    no_predictions: "Aucun prono pour le moment",
    be_first: "Sois le premier à créer un prono pour ta ligue !",
    types: {
      yes_no: "Oui/Non",
      multiple: "Choix multiple",
      free_text: "Texte libre",
    },
    finished: "Terminée",
    set_answer: "Définir la réponse",
    answer_saved: "Réponse enregistrée !",
    answer_success: "Bonne réponse définie ! Points attribués.",
    answer_recorded: "Réponse enregistrée !",
    error_generic: "Erreur",
    correct_answer: "Bonne réponse :",
    your_answer: "Ta réponse : {{answer}}",
    submit_answer: "Envoyer ma réponse",
    answer_pending: "Réponse enregistrée - En attente du résultat",
    your_answer_placeholder: "Ta réponse...",
    create_modal: {
      title: "Créer un prono",
      question: "Question",
      question_placeholder: "Ex: Qui finira devant, Hamilton ou Leclerc ?",
      answer_type: "Type de réponse",
      types: {
        yes_no: "Oui/Non",
        text: "Texte",
        choice: "Choix",
      },
      options: "Options",
      multi_answer: "Multi-réponse",
      option_placeholder: "Option {{index}}",
      add_option: "Ajouter une option",
      creating: "Création...",
      create: "Créer le prono",
      created: "Prono créé !",
      error_question: "Ajoute une question",
      error_choices: "Ajoute au moins 2 choix",
    },
    yes: "Oui",
    no: "Non",
    delete_title: "Supprimer les pronostics",
    delete_warning:
      "Tes pronostics pour <1>{{race}}</1> seront définitivement supprimés. Cette action est irréversible.",
    deleting: "Suppression...",
    delete: "Supprimer",
  },

  // ── Driver comparison ────────────────────────────
  driver_comparison: {
    title: "Comparateur",
    subtitle: "Compare les stats de 2 pilotes",
    swap: "Inverser",
    driver_placeholder: "Pilote",
    select_two: "Sélectionne deux pilotes différents pour les comparer",
    f1_stats: "Stats F1",
    world_titles: "Titres mondiaux",
    victories: "Victoires",
    podiums: "Podiums",
    poles: "Poles",
    fastest_laps: "Meilleurs tours",
    career_points: "Points en carrière",
    grand_prix: "Grands Prix",
    efficiency: "Efficacité",
    win_rate: "Taux de victoire",
    podium_rate: "Taux de podium",
    pole_rate: "Taux de pole",
    points_per_race: "Points/course",
    quick_verdict: "Verdict rapide",
    titles: "Titres",
    wins: "Vict.",
    vs: "VS",
    verdict: {
      dominant:
        "{{name}} domine dans {{count}} catégories sur 7. Son expérience et ses statistiques parlent d'elles-mêmes.",
      slight:
        "{{name}} domine dans {{count}} catégories sur 7. Son profil statistique montre un net avantage.",
      tied: "Ces deux pilotes sont très proches ! Le résultat d'un duel serait imprévisible.",
    },
    aria_view_profile: "Voir la fiche de {{name}}",
  },

  // ── Badges ───────────────────────────────────────
  badges: {
    title: "Badges",
    locked: "(non débloqué)",
    names: {
      beginner: "Débutant",
      regular: "Régulier",
      veteran: "Vétéran",
      scorer: "Scoreur",
      expert: "Expert",
      streak: "En série",
      unstoppable: "Inarrêtable",
      champion: "Champion",
      legend: "Légende",
    },
    descriptions: {
      beginner: "Premier pronostic",
      regular: "5 pronostics",
      veteran: "20 pronostics",
      scorer: "100 points au total",
      expert: "500 points au total",
      streak: "3 GP de suite",
      unstoppable: "5 GP de suite",
      champion: "Top 1 d'une ligue",
      legend: "1000 points au total",
    },
  },

  // ── Error boundary ───────────────────────────────
  error_boundary: {
    title: "Drapeau rouge — erreur système",
    subtitle: "Incident en piste",
    description: "Une erreur inattendue s'est produite. L'équipe technique a été prévenue.",
    radio_label: "Radio équipe",
    radio_message: "On a un souci technique, on est dessus...",
    retry: "Relancer",
    back_home: "Retour au paddock",
  },

  // ── Network status ───────────────────────────────
  network: {
    offline: "Hors ligne — les données se synchroniseront à la reconnexion",
    online: "Connexion rétablie",
  },

  // ── Circuit map ──────────────────────────────────
  circuit_map: {
    filters: {
      all: "Tous",
      turns: "Virages",
      drs: "DRS",
      sectors: "Secteurs",
    },
    features: {
      start: "Départ",
      turn: "Virage",
      drs_zone: "Zone DRS",
      sector: "Secteur",
      zone: "Zone",
    },
    badges: {
      interactive: "Interactive",
      static: "Statique",
    },
    stats: {
      hotspots: "Hotspots",
      points: "Points",
      zones: "Zones",
    },
    first_corner: "Premier virage",
    interactive_label: "Carte interactive {{name}}",
    circuit_label: "Circuit {{name}}",
  },

  // ── Not found (404) ──────────────────────────────
  not_found_page: {
    banner: "Drapeau rouge — session interrompue",
    title: "Drapeau rouge",
    description: "Page introuvable. La session a été interrompue — retourne en piste !",
    radio_label: "Radio équipe",
    radio_message: '"Fred, on a perdu ta position. Rentre aux stands et on te remet en piste."',
    radio_engineer: "— Ingénieur de course",
    back_paddock: "Retour au paddock",
    report_issue: "Signaler un problème",
  },

  // ── Reward celebration ───────────────────────────
  reward: {
    level: "Niveau {{level}} !",
    tap_continue: "Touche pour continuer",
  },

  // ── New results banner ───────────────────────────
  new_results: {
    title: "Résultats disponibles !",
  },

  // ── Race detail hero ─────────────────────────────
  race_hero: {
    sprint_badge: "Week-end Sprint",
    countdown: {
      d: "j",
      h: "h",
      m: "m",
      s: "s",
    },
    status: {
      finished: "Terminé",
      live: "En direct",
      cancelled: "Course annulée",
    },
    cta: {
      results: "Voir les résultats",
      locked: "Pronostics verrouillés",
      edit: "Modifier mes pronostics",
      predict: "Pronostiquer",
      follow_live: "Suivre les résultats en direct",
    },
  },

  // ── Social proof ─────────────────────────────────
  social_proof: {
    text: "joueurs ont déjà soumis leurs pronos",
  },

  // ── Driver card ──────────────────────────────────
  driver_card: {
    win_one: "victoire",
    win_other: "victoires",
  },

  // ── Streak widget ────────────────────────────────
  streak: {
    title: "Série de {{count}} jours | Record : {{record}}",
    days_short: "j",
  },

  // ── Verify email ────────────────────────────────
  verify_email: {
    loading: "Vérification en cours...",
    success_title: "Email vérifié !",
    success_link: "Retour au tableau de bord",
    error_title: "Erreur",
    error_link: "Retour",
    invalid_link: "Lien de vérification invalide",
    success_message: "Ton email a été vérifié avec succès !",
    invalid_or_used: "Ce lien est invalide ou a déjà été utilisé",
  },

  // ── Driver detail ───────────────────────────────
  driver_detail: {
    not_found: "Pilote introuvable",
    load_error: "Impossible de charger les infos du pilote",
    back_label: "Retour",
    drivers_label: "Pilotes",
    compare_label: "Comparer",
    compare_tooltip: "Comparer avec un autre pilote",
    nav_drivers_aria: "Navigation pilotes",
    prev_driver: "Pilote précédent",
    next_driver: "Pilote suivant",
    follow: "Suivi",
    followed: "Pilote suivi !",
    season: "Saison {{year}}",
    ranking: "Classement",
    podiums: "Podiums",
    best_result: "Meilleur résultat",
    avg_points: "Points moyen",
    victories: "victoires",
    strengths: "Points forts",
    speed: "Vitesse",
    overtaking: "Dépassements",
    consistency: "Régularité",
    tyre_mgmt: "Gestion pneus",
    qualifying: "Qualifications",
    f1_career: "Carrière F1",
    titles: "Titres",
    wins: "Victoires",
    poles: "Poles",
    fastest_laps: "Meilleurs tours",
    gp_entered: "GP disputés",
    predict_performance: "Pronostiquez ses performances",
    predict_desc: "Gagnez des points en anticipant ses résultats",
    predict_cta: "Pronostiquer",
    team_contract: "Équipe & Contrat",
    contract_until: "Contrat jusqu'en {{year}}",
    useful_info: "Infos utiles",
    did_you_know: "Le saviez-vous ?",
    age: "Âge",
    height: "Taille",
    since: "Depuis",
    years_suffix: "ANS",
    recent_results: "Derniers résultats",
    see_all: "Voir tout",
    no_results: "Aucun résultat disponible",
    position_suffix: "{{pos}}e",
    position_1: "1er",
    position_2: "2e",
    position_3: "3e",
  },

  // ── Points history ──────────────────────────────
  points_history: {
    title: "Historique des points",
    races_with_results: "{{count}} courses avec résultats",
    no_history: "Aucun historique disponible",
    no_history_desc: "Fais des pronostics pour voir ton historique",
    pending: "En attente",
    pole: "Pole",
    top10_quali: "Top 10 Quali",
    winner: "Vainqueur",
    top10_race: "Top 10 Course",
    sprint_quali: "Sprint Quali",
    sprint_race: "Sprint Course",
    bonus: "Bonus (SC, DNF, Tour, T1)",
  },
};

export default fr;
